import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CommonFormLayout from "../OptmisedCode/Form/CommonFormLayout";
import { MAIN_URL } from "../../Configurations/Urls";

const BASE_ROUTE = "/editions/access";

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function ApplicationEditionForm({ mode }) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = mode === "edit";
  const isView = mode === "view";
  const preselectedAppId = searchParams.get("application_id");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applications, setApplications] = useState([]);
  const [modules, setModules] = useState([]);
  const [applicationId, setApplicationId] = useState(preselectedAppId || "");
  const [editionName, setEditionName] = useState("");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [enabledModuleIds, setEnabledModuleIds] = useState(() => new Set());
  const [enabledFeatureIds, setEnabledFeatureIds] = useState(() => new Set());
  const [expandedModules, setExpandedModules] = useState({});

  const loadCatalog = useCallback(async (appId, editionId) => {
    const params = {};
    if (appId) params.application_id = appId;
    if (editionId) params.application_edition_id = editionId;
    const res = await axios.get(`${MAIN_URL}/api/core/application-editions`, {
      headers: authHeaders(),
      params,
    });
    return res.data || {};
  }, []);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      setLoading(true);
      try {
        if ((isEdit || isView) && id) {
          const res = await axios.get(
            `${MAIN_URL}/api/core/application-editions/${id}`,
            { headers: authHeaders() }
          );
          if (cancelled) return;
          const data = res.data || {};
          const edition = data.edition || {};
          setApplications(data.applications || []);
          setModules(data.modules || []);
          setApplicationId(String(edition.application_id || ""));
          setEditionName(edition.edition_name || edition.name || "");
          setCode(edition.code || "");
          setDescription(edition.description || "");
          setIsDefault(Boolean(edition.is_default));
          setIsActive(edition.is_active !== false);
          setEnabledModuleIds(new Set((data.enabled_module_ids || []).map(Number)));
          setEnabledFeatureIds(new Set((data.enabled_feature_ids || []).map(Number)));
        } else {
          const data = await loadCatalog(preselectedAppId);
          if (cancelled) return;
          setApplications(data.applications || []);
          setModules(data.modules || []);
          if (preselectedAppId) {
            setApplicationId(String(preselectedAppId));
          }
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err.response?.data?.error ||
              err.response?.data?.message ||
              err.message ||
              "Failed to load edition."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, isView, loadCatalog, preselectedAppId]);

  useEffect(() => {
    const expanded = {};
    modules.forEach((mod) => {
      expanded[mod.module_id] = true;
    });
    setExpandedModules(expanded);
  }, [modules]);

  const handleApplicationChange = async (nextApplicationId) => {
    setApplicationId(nextApplicationId);
    setEnabledModuleIds(new Set());
    setEnabledFeatureIds(new Set());
    if (!nextApplicationId) {
      setModules([]);
      return;
    }
    try {
      const data = await loadCatalog(nextApplicationId);
      setModules(data.modules || []);
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.message ||
          "Failed to load application catalog."
      );
    }
  };

  const toggleModule = (moduleId, features) => {
    setEnabledModuleIds((prevModules) => {
      const nextModules = new Set(prevModules);
      const turningOn = !nextModules.has(moduleId);
      if (turningOn) nextModules.add(moduleId);
      else nextModules.delete(moduleId);

      setEnabledFeatureIds((prevFeatures) => {
        const nextFeatures = new Set(prevFeatures);
        (features || []).forEach((feature) => {
          if (turningOn) nextFeatures.add(feature.module_feature_id);
          else nextFeatures.delete(feature.module_feature_id);
        });
        return nextFeatures;
      });

      return nextModules;
    });
  };

  const toggleFeature = (featureId, moduleId) => {
    setEnabledFeatureIds((prev) => {
      const next = new Set(prev);
      const turningOn = !next.has(featureId);
      if (turningOn) {
        next.add(featureId);
        setEnabledModuleIds((prevModules) => {
          const nextModules = new Set(prevModules);
          nextModules.add(moduleId);
          return nextModules;
        });
      } else {
        next.delete(featureId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!applicationId) {
      toast.error("Application is required.");
      return;
    }
    if (!editionName.trim() || !code.trim()) {
      toast.error("Edition name and code are required.");
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${MAIN_URL}/api/core/application-editions`,
        {
          application_id: Number(applicationId),
          application_edition_id: isEdit && id ? Number(id) : null,
          edition_name: editionName.trim(),
          code: code.trim(),
          description: description.trim() || null,
          is_default: isDefault,
          is_active: isActive,
          modules: modules.map((mod) => ({
            module_id: mod.module_id,
            is_enabled: enabledModuleIds.has(mod.module_id),
          })),
          features: modules.flatMap((mod) =>
            (mod.features || []).map((feature) => ({
              module_feature_id: feature.module_feature_id,
              is_enabled:
                enabledModuleIds.has(mod.module_id) &&
                enabledFeatureIds.has(feature.module_feature_id),
            }))
          ),
        },
        { headers: authHeaders() }
      );
      toast.success(isEdit ? "Edition updated." : "Edition saved.");
      navigate(BASE_ROUTE);
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Save failed."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <CommonFormLayout
      mode={mode}
      loading={loading}
      btnLoading={saving}
      onSubmit={handleSave}
      size="wide"
      wrapInGrid={false}
      submitDisabled={!applicationId}
      headerProps={{
        addMessage: "Add application edition",
        updateMessage: "Application editions",
        homeLink: BASE_ROUTE,
        homeText: "Application editions",
        editLink: id ? `${BASE_ROUTE}/edit/${id}` : undefined,
      }}
    >
            <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
              <FormControl sx={{ minWidth: 260 }} size="small" disabled={isEdit || isView}>
                <InputLabel id="edition-app-label">Application</InputLabel>
                <Select
                  labelId="edition-app-label"
                  label="Application"
                  value={applicationId}
                  onChange={(event) => handleApplicationChange(event.target.value)}
                >
                  {applications.map((app) => (
                    <MenuItem
                      key={app.application_id}
                      value={String(app.application_id)}
                    >
                      {app.application_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Edition name"
                size="small"
                sx={{ minWidth: 240 }}
                value={editionName}
                InputProps={{ readOnly: isView }}
                onChange={(event) => setEditionName(event.target.value)}
              />
              <TextField
                label="Code"
                size="small"
                sx={{ minWidth: 180 }}
                value={code}
                InputProps={{ readOnly: isView }}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
                helperText="Unique per application, e.g. PROLEED or CLIENT"
              />
            </Box>

            <TextField
              label="Description"
              size="small"
              fullWidth
              sx={{ mb: 2 }}
              value={description}
              InputProps={{ readOnly: isView }}
              onChange={(event) => setDescription(event.target.value)}
            />

            <Box display="flex" gap={3} mb={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isDefault}
                    disabled={isView}
                    onChange={(event) => setIsDefault(event.target.checked)}
                  />
                }
                label="Default edition"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    disabled={isView}
                    onChange={(event) => setIsActive(event.target.checked)}
                  />
                }
                label="Active"
              />
            </Box>

            {!applicationId ? (
              <Alert severity="info">Select an application to load modules.</Alert>
            ) : modules.length === 0 ? (
              <Alert severity="info">
                No modules are assigned to this application yet. Save them first
                on Application Module Features.
              </Alert>
            ) : (
              <>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Modules and features
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                  Turn a module off to disable every feature under it.
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {modules.map((mod) => {
                  const moduleOn = enabledModuleIds.has(mod.module_id);
                  const enabledCount = (mod.features || []).filter((feature) =>
                    moduleOn && enabledFeatureIds.has(feature.module_feature_id)
                  ).length;
                  return (
                    <Accordion
                      key={mod.module_id}
                      expanded={Boolean(expandedModules[mod.module_id])}
                      onChange={() =>
                        setExpandedModules((prev) => ({
                          ...prev,
                          [mod.module_id]: !prev[mod.module_id],
                        }))
                      }
                      sx={{
                        m: 0,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: "8px !important",
                        boxShadow: "none",
                        "&:before": { display: "none" },
                        "&.Mui-expanded": { m: 0 },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            width: "100%",
                            pr: 2,
                          }}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Checkbox
                            size="small"
                            checked={moduleOn}
                            disabled={isView}
                            onChange={() => toggleModule(mod.module_id, mod.features)}
                          />
                          <Typography variant="subtitle1" fontWeight={600}>
                            {mod.module_name}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${enabledCount}/${(mod.features || []).length} features`}
                            color={moduleOn ? "primary" : "default"}
                            variant="outlined"
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {(mod.features || []).length === 0 ? (
                          <Typography color="text.secondary">
                            No features for this module.
                          </Typography>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1.5,
                              pl: 5,
                            }}
                          >
                            {(mod.features || []).map((feature) => (
                              <FormControlLabel
                                key={feature.module_feature_id}
                                sx={{
                                  m: 0,
                                  minWidth: 220,
                                  border: 1,
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  px: 1,
                                  py: 0.5,
                                }}
                                control={
                                  <Checkbox
                                    size="small"
                                    disabled={isView || !moduleOn}
                                    checked={
                                      moduleOn &&
                                      enabledFeatureIds.has(feature.module_feature_id)
                                    }
                                    onChange={() =>
                                      toggleFeature(
                                        feature.module_feature_id,
                                        mod.module_id
                                      )
                                    }
                                  />
                                }
                                label={
                                  <Typography variant="body2">
                                    {feature.module_feature_name}
                                  </Typography>
                                }
                              />
                            ))}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
                </Box>
              </>
            )}

    </CommonFormLayout>
  );
}
